/**
 * Axios client with subdomain interceptor
 * 
 * Automatically prepends subdomain from Redux store to all relative API URLs.
 * This ensures all API calls include the subdomain when present.
 * 
 * Examples:
 * - /users/sign_in -> /mystore/users/sign_in (if subdomain exists)
 * - http://external.com/api -> unchanged (absolute URLs are not modified)
 */
import axios from 'axios';
import { getCurrentSubdomain, getSubdomainFromStore } from '../../helpers/pathUtils';

const client = axios.create({
  baseURL: '/',
  responseType: 'json'
});

// Request interceptor to prepend subdomain to relative URLs
client.interceptors.request.use(
  (config) => {
    // Skip if URL is absolute (starts with http/https)
    if (!config.url || config.url.startsWith('http')) {
      return config;
    }
    
    // Get subdomain from Redux store
    // The subdomain is set in the store from the HTML data attribute before React renders
    // (see packs/index.tsx), so it should always be available here
    let subdomain = getSubdomainFromStore();
    
    // Fallback: If not in store (shouldn't happen, but defensive programming),
    // try to get from HTML data attribute as a last resort
    if (!subdomain) {
      const rootElement = document.getElementById('root');
      if (rootElement) {
        const dataSubdomain = rootElement.dataset.subdomain;
        if (dataSubdomain && dataSubdomain !== 'undefined' && dataSubdomain !== '') {
          subdomain = dataSubdomain;
        }
      }
    }
    
    // If subdomain exists, prepend to URL
    if (subdomain) {
      // Remove leading slash from URL if present
      const urlWithoutSlash = config.url.startsWith('/') ? config.url.slice(1) : config.url;
      config.url = `/${subdomain}/${urlWithoutSlash}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default client;
